import React, { useState, useEffect } from 'react';
import { toolsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const statusClasses = {
  available: 'bg-emerald-100 text-emerald-700',
  checked_out: 'bg-blue-100 text-blue-700',
  overdue: 'bg-red-100 text-red-700',
  maintenance: 'bg-amber-100 text-amber-700',
  calibration: 'bg-orange-100 text-orange-700',
  retired: 'bg-slate-100 text-slate-600',
};
const conditionClasses = {
  excellent: 'bg-emerald-100 text-emerald-700',
  good: 'bg-blue-100 text-blue-700',
  fair: 'bg-amber-100 text-amber-700',
  poor: 'bg-red-100 text-red-700',
};
const Badge = ({ status, map }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${(map || statusClasses)[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>
);

const inp = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent';
const sel = `${inp} bg-white`;
const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold text-slate-600 mb-1">{children}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
);

const ToolManagement = () => {
  const [tools, setTools] = useState([]);
  const [filteredTools, setFilteredTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTool, setCurrentTool] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { isAdmin, isSupervisor } = useAuth();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    productNumber: '', serialNumber: '', toolName: '', description: '',
    category: '', storageLocation: '', status: 'available',
    purchaseDate: '', purchasePrice: '', condition: 'good', notes: ''
  });

  useEffect(() => { fetchTools(); }, []);
  useEffect(() => { filterTools(); }, [tools, searchTerm, statusFilter, categoryFilter]);

  const fetchTools = async () => {
    try {
      const response = await toolsAPI.getAll();
      setTools(response.data.tools || []);
    } catch {
      toast('Failed to load tools', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterTools = () => {
    let filtered = [...tools];
    if (searchTerm) {
      filtered = filtered.filter(tool =>
        tool.tool_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.product_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== 'all') filtered = filtered.filter(tool => tool.status === statusFilter);
    if (categoryFilter !== 'all') filtered = filtered.filter(tool => tool.category === categoryFilter);
    setFilteredTools(filtered);
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleAdd = () => {
    setEditMode(false); setCurrentTool(null);
    setFormData({ productNumber: '', serialNumber: '', toolName: '', description: '', category: '', storageLocation: '', status: 'available', purchaseDate: '', purchasePrice: '', condition: 'good', notes: '' });
    setShowModal(true);
  };

  const handleEdit = (tool) => {
    setEditMode(true); setCurrentTool(tool);
    setFormData({
      productNumber: tool.product_number || '', serialNumber: tool.serial_number || '',
      toolName: tool.tool_name || '', description: tool.description || '',
      category: tool.category || '', storageLocation: tool.storage_location || '',
      status: tool.status || 'available',
      purchaseDate: tool.purchase_date ? tool.purchase_date.split('T')[0] : '',
      purchasePrice: tool.purchase_price || '', condition: tool.condition || 'good', notes: tool.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editMode) {
        await toolsAPI.update(currentTool._id, formData);
        toast('Tool updated successfully!', 'success');
      } else {
        await toolsAPI.create(formData);
        toast('Tool added successfully!', 'success');
      }
      setShowModal(false);
      fetchTools();
    } catch (err) {
      const e = err.response?.data?.error;
      toast(typeof e === 'string' ? e : e?.message || 'Operation failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (toolId, toolName) => {
    if (!window.confirm(`Retire "${toolName}"? This cannot be undone.`)) return;
    try {
      await toolsAPI.retire(toolId);
      toast(`"${toolName}" has been retired`, 'warning');
      fetchTools();
    } catch (err) {
      const e = err.response?.data?.error;
      toast(typeof e === 'string' ? e : e?.message || 'Failed to retire tool', 'error');
    }
  };

  const categories = [...new Set(tools.map(t => t.category).filter(Boolean))];

  if (loading) return (
    <div className="flex items-center justify-center min-h-96">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-brand-primary rounded-full animate-spin"></div>
        <p className="text-sm text-slate-500">Loading tools...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Search by name, serial or product number..."
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`${sel} w-auto`}>
            <option value="all">All Status</option>
            <option value="available">Available</option>
            <option value="checked_out">Checked Out</option>
            <option value="maintenance">Maintenance</option>
            <option value="calibration">Calibration</option>
            <option value="retired">Retired</option>
          </select>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className={`${sel} w-auto`}>
            <option value="all">All Categories</option>
            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
        {(isAdmin || isSupervisor) && (
          <button onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white text-sm font-semibold rounded-lg transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Tool
          </button>
        )}
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="font-semibold text-slate-900">Tools Inventory</h3>
          </div>
          <span className="px-2.5 py-0.5 bg-brand-primary-bg text-brand-primary text-xs font-semibold rounded-full">{filteredTools.length}</span>
        </div>

        {filteredTools.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            </svg>
            <p className="text-slate-500 font-medium">No tools found</p>
            <p className="text-slate-400 text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {['Tool', 'Serial Number', 'Category', 'Location', 'Status', 'Condition', ...(isAdmin || isSupervisor ? ['Actions'] : [])].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTools.map(tool => (
                  <tr key={tool._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900 text-sm">{tool.tool_name}</p>
                      <p className="text-xs text-slate-500">{tool.product_number}</p>
                    </td>
                    <td className="px-4 py-3"><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{tool.serial_number}</code></td>
                    <td className="px-4 py-3 text-sm text-slate-700">{tool.category}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{tool.storage_location || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><Badge status={tool.status} map={statusClasses} /></td>
                    <td className="px-4 py-3"><Badge status={tool.condition} map={conditionClasses} /></td>
                    {(isAdmin || isSupervisor) && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleEdit(tool)} title="Edit"
                            className="p-1.5 text-slate-500 hover:text-brand-primary hover:bg-brand-primary-bg rounded-lg transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDelete(tool._id, tool.tool_name)} title="Retire"
                              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900">{editMode ? 'Edit Tool' : 'Add New Tool'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="overflow-y-auto flex-1 p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label required>Product Number</Label>
                    <input type="text" name="productNumber" value={formData.productNumber} onChange={handleInputChange} required className={inp} />
                  </div>
                  <div>
                    <Label required>Serial Number</Label>
                    <input type="text" name="serialNumber" value={formData.serialNumber} onChange={handleInputChange} required disabled={editMode}
                      className={`${inp} ${editMode ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''}`} />
                  </div>
                  <div className="col-span-2">
                    <Label required>Tool Name</Label>
                    <input type="text" name="toolName" value={formData.toolName} onChange={handleInputChange} required className={inp} />
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} className={inp} />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <input type="text" name="category" value={formData.category} onChange={handleInputChange} placeholder="e.g., Electrical, Mechanical" className={inp} />
                  </div>
                  <div>
                    <Label>Storage Location</Label>
                    <input type="text" name="storageLocation" value={formData.storageLocation} onChange={handleInputChange} placeholder="e.g., Warehouse A, Shelf 3" className={inp} />
                  </div>
                  <div>
                    <Label required>Status</Label>
                    <select name="status" value={formData.status} onChange={handleInputChange} className={sel}>
                      <option value="available">Available</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="calibration">Calibration</option>
                      <option value="retired">Retired</option>
                    </select>
                  </div>
                  <div>
                    <Label>Condition</Label>
                    <select name="condition" value={formData.condition} onChange={handleInputChange} className={sel}>
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                  <div>
                    <Label>Purchase Date</Label>
                    <input type="date" name="purchaseDate" value={formData.purchaseDate} onChange={handleInputChange} className={inp} />
                  </div>
                  <div>
                    <Label>Purchase Price</Label>
                    <input type="number" name="purchasePrice" value={formData.purchasePrice} onChange={handleInputChange} placeholder="0.00" step="0.01" className={inp} />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={2} className={inp} />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-slate-700 text-sm font-medium rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                  {submitting ? 'Saving...' : editMode ? 'Update Tool' : 'Add Tool'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolManagement;
